import {useEffect, useState} from "react";
import {useRouter} from "next/router";
import FilterBase from "@/components/filters/FilterBase";
import styles from "@/components/filters/PriceFilter.module.css";
import * as d3 from "d3";
import {authenticatedFetchAPI} from "@/pages/api/clearwattApi";
import ButtonComponent from "@/components/ui/buttons/ButtonComponent";
import SliderPills from "@/components/ui/sliders/SliderPills";

const PriceFilter = () => {
  const router = useRouter();
  const buttonName = 'Price Range';
  const filterName = 'price';
  const [chartRef, setChartRef] = useState(null);
  const [data, setData] = useState(null);

  // Initial minimum and maximum average_range values
  const [initialMinValue, setInitialMinValue] = useState(0);
  const [initialMaxValue, setInitialMaxValue] = useState(4);

// Current minimum and maximum values (controlled by the slider)
  const [currentMinValue, setCurrentMinValue] = useState(0);
  const [currentMaxValue, setCurrentMaxValue] = useState(4);


  const priceCategories = {
    '0': '£',
    '1': '££',
    '2': '£££',
    '3': '££££',
    '4': '£££££'
  };


  const generateChart = (data, chart) => {
    // calculate the dimensions
    const width = chart.offsetWidth;
    const height = chart.offsetHeight;
    const padding = 0;

    // create a linear scale for the y axis (count)
    const yScale = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.count)])
      .range([height - padding, padding]);

    // create a band scale for the x axis (average range)
    const xScale = d3.scaleBand()
      .domain(data.map(d => d.price_category))
      .range([padding, width - padding])
      .padding(0.1);

    // generate the svg
    const svg = d3.select(chart).append("svg")
      .attr('viewBox', `0 0 ${width} ${height}`);

    // append g for x axis
    /*const gX = svg.append("g")
      .attr("transform", "translate(0," + (height - padding) + ")") // move it to the bottom of the svg
      .call(d3.axisBottom(xScale));*/ // draw the x axis using the x scale

    // generate the bars for the distribution
    svg.selectAll("rect")
      .data(data)
      .enter().append("rect")
      .attr('x', d => xScale(d.price_category))
      .attr('y', d => yScale(d.count))
      .attr('width', xScale.bandwidth())
      .attr('height', d => height - yScale(d.count) - padding)
      .style('fill', 'var(--secondary-color)');

  };


  /*useEffect(() => {
    authenticatedFetchAPI("/api/price_custom_distribution")
      .then(data => {
        // Extract all the price intervals
        const intervals = data.map(d => d.price_interval);

        // Get the minimum and maximum values from the intervals
        const minPrice = Math.min(...intervals);
        const maxPrice = Math.max(...intervals);

        // Use the `priceCategories` mapping to get the representation of the price
        const minPriceRepresentation = priceCategories[minPrice];
        const maxPriceRepresentation = priceCategories[maxPrice];

        setInitialMinValue(minPrice);
        setInitialMaxValue(maxPrice);

        // Once you've set the initial values, check the router's query
        if (router.query.price) {
          const [min, max] = router.query.price.split('-').map(Number);
          setCurrentMinValue(min);
          setCurrentMaxValue(max);
        } else {
          setCurrentMinValue(minPrice);
          setCurrentMaxValue(maxPrice);
        }

        setData(data);
      })
      .catch((error) => {
        console.error("There has been a problem with your fetch operation:", error);
      });
  }, [router.query.price]);*/

  const fetchData = async () => {
    try {
      const apiUrl = `${process.env.NEXT_PUBLIC_CLEARWATT_BASE_API}/vehicles/price_custom_distribution/`;
      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }
      const data = await response.json();

      // Filter out the category with an empty value
      const filteredData = data.filter(item => item.price_category !== "");

      // Extract all the price intervals from the filtered data
      const intervals = filteredData.map(d => d.price_category);

      // Get the minimum and maximum values from the intervals
      const minPrice = Math.min(...intervals);
      const maxPrice = Math.max(...intervals);

      // Use the `priceCategories` mapping to get the representation of the price
      const minPriceRepresentation = priceCategories[minPrice];
      const maxPriceRepresentation = priceCategories[maxPrice];

      setInitialMinValue(minPrice);
      setInitialMaxValue(maxPrice);

      // Once you've set the initial values, check the router's query
      if (router.query.price) {
        const [min, max] = router.query.price.split('-').map(Number);
        setCurrentMinValue(min);
        setCurrentMaxValue(max);
      } else {
        setCurrentMinValue(minPrice);
        setCurrentMaxValue(maxPrice);
      }

      setData(filteredData);
    } catch (error) {
      console.error("Error fetching price distribution:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [router.query.price]);


  const updateBarColors = () => {
    const svg = d3.select(chartRef).select("svg");
    svg.selectAll("rect")
      .transition()
      .duration(500)
      .style('fill', d => {
        if (d.price_category >= currentMinValue && d.price_category <= currentMaxValue) {
          return 'var(--secondary-color)';
        }
        return 'var(--grey-2-color)';
      });
  };

  useEffect(() => {
    if (chartRef) {
      updateBarColors();
    }
  }, [currentMinValue, currentMaxValue]);


  useEffect(() => {
    if (typeof document !== "undefined") {
      if (data && chartRef) {
        generateChart(data, chartRef);
      }
    }

    // Clean up
    return () => {
      if (chartRef) {
        d3.select(chartRef).selectAll("svg").remove();
      }
    };
  }, [data, chartRef]);


  const handleOptionSelected = (optionValue) => {
    const {query} = router;
    router.push({
      pathname: router.pathname,
      query: {...query, [filterName]: optionValue},
    });
  };

  const handleSliderChange = (values) => {
    setCurrentMinValue(values[0]);
    setCurrentMaxValue(values[1]);
  };

  const handleFilterClick = () => {
    const {query} = router;
    const newPrice = `${currentMinValue}-${currentMaxValue}`;
    router.push({
      pathname: router.pathname,
      query: {...query, price: newPrice},
    });

    close(); // Close the FilterBase component
  };

  return (
    <FilterBase buttonName={buttonName}>
      {({close}) => (
        <div className={styles.filters}>
          <div className={styles.subFilters}>
            <div ref={setChartRef} style={{width: '100%', height: '56px'}}></div>
            <div className={styles.slider}>
              <SliderPills min={initialMinValue} max={initialMaxValue} value={[currentMinValue, currentMaxValue]}
                           onChange={handleSliderChange}/>
            </div>

            <div className={styles.controls}>
              <ButtonComponent
                icon="🔍"
                iconPosition="left"
                text="Filter"
                onClick={() => {
                  handleFilterClick();
                  close();
                }}
              />
            </div>
          </div>
        </div>
      )}
    </FilterBase>
  );
};

export default PriceFilter;